/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Shop } from '../services/shopService';
import { getValidImageUrl } from '../utils/imageUtils';
import { useTranslations } from '../hooks/useTranslations';
import { useAuth } from '../contexts/AuthContext';
import './FavoriteShopsPage.css';

const FavoriteShopsPage: React.FC = () => {
  const navigate = useNavigate();
  const t = useTranslations();
  const { user, isAuthenticated } = useAuth();
  const [favoriteShopIds, setFavoriteShopIds] = useState<string[]>([]);
  const [favoriteShops, setFavoriteShops] = useState<Shop[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load favorite shops from API or localStorage on mount
  useEffect(() => {
    loadFavoriteShopIds();
  }, [isAuthenticated, user?.id]);

  // Load shop details when IDs change
  useEffect(() => {
    if (favoriteShopIds.length > 0) {
      loadFavoriteShops();
    } else {
      setFavoriteShops([]);
      setIsLoading(false);
    }
  }, [favoriteShopIds]);

  const loadFavoriteShopIds = async () => {
    try {
      setIsLoading(true);
      if (isAuthenticated && user?.id) {
        // Load from API for authenticated users
        const shopIds = await apiService.getFavoriteShops(user.id);
        setFavoriteShopIds(shopIds);
      } else {
        // Fallback to localStorage for guests
        try {
          const saved = localStorage.getItem('favorite_shops');
          setFavoriteShopIds(saved ? JSON.parse(saved) : []);
        } catch {
          setFavoriteShopIds([]);
        }
      }
    } catch (error) {
      console.error('Error loading favorite shop IDs:', error);
      // Fallback to localStorage
      try {
        const saved = localStorage.getItem('favorite_shops');
        setFavoriteShopIds(saved ? JSON.parse(saved) : []);
      } catch {
        setFavoriteShopIds([]);
      }
    }
  };

  const loadFavoriteShops = async () => {
    try {
      setIsLoading(true);
      if (favoriteShopIds.length === 0) {
        setFavoriteShops([]);
        return;
      }

      // Load all shops and filter by favorite IDs
      const allShops = await apiService.getShops();
      const favorites = allShops.filter(shop => 
        favoriteShopIds.includes(String(shop.id))
      );
      setFavoriteShops(favorites);
    } catch (error) {
      console.error('Error loading favorite shops:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFavorite = async (shopId: string) => {
    if (!isAuthenticated || !user?.id) {
      // Fallback to localStorage for guests
      const updated = favoriteShopIds.includes(shopId)
        ? favoriteShopIds.filter(id => id !== shopId)
        : [...favoriteShopIds, shopId];
      
      setFavoriteShopIds(updated);
      localStorage.setItem('favorite_shops', JSON.stringify(updated));
      return;
    }

    try {
      if (favoriteShopIds.includes(shopId)) {
        // Remove from favorites via API
        await apiService.removeFavoriteShop(user.id, shopId);
        const updated = favoriteShopIds.filter(id => id !== shopId);
        setFavoriteShopIds(updated);
        console.log('✅ Removed shop from favorites:', shopId);
      } else {
        // Add to favorites via API
        await apiService.addFavoriteShop(user.id, shopId);
        const updated = [...favoriteShopIds, shopId];
        setFavoriteShopIds(updated);
        console.log('✅ Added shop to favorites:', shopId);
      }
    } catch (error) {
      console.error('Error updating favorite shop:', error);
      // Fallback to localStorage on error
      const updated = favoriteShopIds.includes(shopId)
        ? favoriteShopIds.filter(id => id !== shopId)
        : [...favoriteShopIds, shopId];
      
      setFavoriteShopIds(updated);
      localStorage.setItem('favorite_shops', JSON.stringify(updated));
    }
  };

  const handleShopClick = (shop: Shop) => {
    navigate(`/shop/${shop.id}`);
  };

  const getShopTypeIcon = (type: string) => {
    const icons: { [key: string]: string } = {
      'grocery': '🛒',
      'medical': '🏥',
      'pharmacy': '💊',
      'electronics': '📱',
      'clothing': '👕',
      'bakery': '🥖',
      'restaurant': '🍛',
      'specialty': '🏪'
    };
    return icons[type] || '🏪';
  };

  return (
    <div className="favorite-shops-page">
      <div className="favorite-shops-body-container">
        <div className="favorite-shops-header">
          <button className="back-btn" onClick={() => navigate('/app')}>
            ← Back to Dashboard
          </button>
          <h1>❤️ My Favorite Shops</h1>
          <div className="favorite-count">{favoriteShops.length} {favoriteShops.length === 1 ? 'shop' : 'shops'}</div>
        </div>

        {isLoading ? (
          <div className="loading-state">Loading favorite shops...</div>
        ) : favoriteShops.length === 0 ? (
          <div className="empty-favorites">
            <div className="empty-icon">❤️</div>
            <h2>No favorite shops yet</h2>
            <p>Start exploring shops and add them to your favorites!</p>
            <button className="explore-btn" onClick={() => navigate('/explore-shops')}>
              Explore Shops
            </button>
          </div>
        ) : (
          <div className="favorite-shops-grid">
          {favoriteShops.map(shop => (
            <div key={shop.id} className="favorite-shop-card">
              <div className="shop-card-header">
                <div className="shop-icon">{getShopTypeIcon(shop.type)}</div>
                <button
                  className={`favorite-btn active`}
                  onClick={() => toggleFavorite(String(shop.id))}
                  aria-label="Remove from favorites"
                  title="Remove from favorites"
                >
                  ❤️
                </button>
              </div>
              
              <div className="shop-card-content" onClick={() => handleShopClick(shop)}>
                <h3>{shop.name}</h3>
                <p className="shop-description">{shop.description || 'No description available'}</p>
                <div className="shop-meta">
                  <span className="shop-type">{shop.type}</span>
                  {shop.is_active ? (
                    <span className="shop-status active">🟢 Open</span>
                  ) : (
                    <span className="shop-status inactive">🔴 Closed</span>
                  )}
                </div>
                {shop.address && (
                  <div className="shop-location">
                    📍 {shop.city}, {shop.state}
                  </div>
                )}
              </div>

              <div className="shop-card-actions">
                <button
                  className="view-shop-btn"
                  onClick={() => handleShopClick(shop)}
                >
                  View Shop
                </button>
                <button
                  className="browse-products-btn"
                  onClick={() => navigate(`/shop/${shop.id}`)}
                >
                  Browse Products
                </button>
              </div>
            </div>
          ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoriteShopsPage;
